import React, { useState, useMemo } from "react";
import "./App.css";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Leaf, Trees, DollarSign, TrendingUp, TrendingDown, ShieldCheck, Bell,
  Map as MapIcon, Camera, LineChart, Activity, ShoppingCart, Search,
  ChevronRight, ExternalLink, Info
} from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar
} from "recharts";

// Hooks & Context
import { useAuth } from "./context/AuthContext";
import { useUserProfile } from "./hooks/useUserProfile";
import { useInvestments } from "./hooks/useInvestments";
import { useCarbonCredits } from "./hooks/useCarbonCredits";
import { useForests } from "./hooks/useForests";
import { useMarketListings } from "./hooks/useMarketListings";
import { useNotifications } from "./hooks/useNotifications";

// Services
import { createBuyOrder, createSellOrder } from "./services/orders.service";

// Components
import LoadingSpinner from "./components/LoadingSpinner";
import Toast from "./components/Toast";

/** ==== Data Placeholders (for charts where backend data might be complex to agg) ==== */
const portfolioSeries = [
  { day: "Mon", value: 120000 },
  { day: "Tue", value: 121500 },
  { day: "Wed", value: 118400 },
  { day: "Thu", value: 123200 },
  { day: "Fri", value: 125800 },
  { day: "Sat", value: 126400 },
  { day: "Sun", value: 128900 },
];

const creditsSeries = [
  { month: "Jan", tco2: 18 },
  { month: "Feb", tco2: 22 },
  { month: "Mar", tco2: 30 },
  { month: "Apr", tco2: 36 },
  { month: "May", tco2: 40 },
  { month: "Jun", tco2: 48 },
];

/** ==== Reusable bits ==== */
const KPI = ({ title, value, delta, up, subValue }) => (
  <div className="card">
    <div className="card-title">{title}</div>
    <div className="kpi-value">
      {value}
      {delta && (
        <span className={up ? "kpi-delta-up" : "kpi-delta-down"}>
          {" "}{delta}
        </span>
      )}
    </div>
    {subValue && <div style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginTop: 4 }}>{subValue}</div>}
  </div>
);

const PortfolioChart = () => (
  <div className="card">
    <div className="card-header">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <LineChart size={16} /> Portfolio Value
      </div>
      <span className="badge">Last 7 days</span>
    </div>
    <div className="chart-lg">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={portfolioSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="pv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#6ee7b7" stopOpacity={0.6}/>
              <stop offset="95%" stopColor="#10b981" stopOpacity={0.05}/>
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
          <XAxis dataKey="day" tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <YAxis tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
          <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1e293b", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
          <Area type="monotone" dataKey="value" stroke="#10b981" fill="url(#pv)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const CreditsChart = ({ carbonWallet }) => {
  // Use real data if available, else placeholder
  // For this example, we'll stick to placeholder as historical data isn't in carbonWallet
  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ShieldCheck size={16}/> Carbon Credits
      </div>
      <div className="chart">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={creditsSeries}>
            <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
            <XAxis dataKey="month" tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
            <YAxis tick={{ fill: "#475569" }} axisLine={{ stroke: "#cbd5e1" }} tickLine={false} />
            <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e2e8f0", color: "#1e293b", boxShadow: "0 4px 6px rgba(0,0,0,0.1)" }} />
            <Bar dataKey="tco2" fill="#6ee7b7" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      {carbonWallet && (
        <div style={{ marginTop: 10, fontSize: "0.9rem", textAlign: "center" }}>
          <span style={{ fontWeight: 600 }}>Pending:</span> {carbonWallet.pendingMassKg?.toFixed(1)} kg / 1000 kg
          <div style={{ width: "100%", background: "#e2e8f0", height: 6, borderRadius: 3, marginTop: 4 }}>
            <div style={{ width: `${(carbonWallet.pendingMassKg / 1000) * 100}%`, background: "#10b981", height: "100%", borderRadius: 3 }} />
          </div>
        </div>
      )}
    </div>
  );
};

const Holdings = ({ investments, forests }) => {
  const navigate = useNavigate();
  
  // Helper to find forest name
  const getForestName = (forestId) => {
    const forest = forests.find(f => f.id === forestId);
    return forest ? forest.name : forestId;
  };

  if (investments.length === 0) {
    return (
      <div className="card">
         <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Trees size={16}/> Holdings
        </div>
        <div style={{ padding: "20px", textAlign: "center", color: "var(--text-muted)" }}>
          No investments yet. Start by exploring forests!
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <Trees size={16}/> Holdings
      </div>
      <div className="table-head">
        <div>Forest</div>
        <div style={{ textAlign: "right" }}>Trees</div>
        <div style={{ textAlign: "right" }}>Status</div>
        <div style={{ textAlign: "right" }}>Value</div>
      </div>
      <div style={{ marginTop: 8, maxHeight: '300px', overflowY: 'auto' }}>
        {investments.map((p, i) => (
          <div 
            key={i} 
            className="table-row"
            onClick={() => navigate(`/plot/${p.plotId}`)}
            style={{ cursor: 'pointer' }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--text-primary)" }}>
              <Leaf size={16} color="#10b981" /> {getForestName(p.forestId)}
            </div>
            <div style={{ textAlign: "right" }}>{p.treeCount}</div>
            <div style={{ textAlign: "right" }}><span className="pill">{p.status}</span></div>
            <div style={{ textAlign: "right" }}>${p.currentValue?.toLocaleString()}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const MarketPanel = ({ listings, onOrder, showToast, availableCredits }) => {
  const [tab, setTab] = useState("buy");
  // New asset type toggle state
  const [assetType, setAssetType] = useState('tree'); // 'tree' or 'carbon_credit'

  // Form states
  const [buyForm, setBuyForm] = useState({ forest: '', quantity: '', price: '' });
  const [sellForm, setSellForm] = useState({ holdingId: '', quantity: '', price: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleBuy = async () => {
    setIsSubmitting(true);
    try {
      // Validate integer constraint for carbon credits
      if (assetType === 'carbon_credit' && !Number.isInteger(Number(buyForm.quantity))) {
        throw new Error("Carbon credits can only be traded in whole tonnes.");
      }

      await createBuyOrder({
        ...buyForm,
        type: 'buy',
        assetType,
        quantity: Number(buyForm.quantity),
        price: Number(buyForm.price)
      });
      showToast(`${assetType === 'tree' ? 'Tree' : 'Carbon Credit'} buy order placed!`, "success");
      setBuyForm({ forest: '', quantity: '', price: '' });
    } catch (error) {
      showToast(error.message || "Failed to place order", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSell = async () => {
    setIsSubmitting(true);
    try {
      // Validate integer constraint for carbon credits
      if (assetType === 'carbon_credit' && !Number.isInteger(Number(sellForm.quantity))) {
        throw new Error("Carbon credits can only be traded in whole tonnes.");
      }
      // Check available balance for credits
      if (assetType === 'carbon_credit' && Number(sellForm.quantity) > availableCredits) {
        throw new Error(`Insufficient credits. Available: ${availableCredits}`);
      }

      await createSellOrder({
        ...sellForm,
        type: 'sell',
        assetType,
        quantity: Number(sellForm.quantity),
        price: Number(sellForm.price)
      });
      showToast(`${assetType === 'tree' ? 'Tree' : 'Carbon Credit'} sell order placed!`, "success");
      setSellForm({ holdingId: '', quantity: '', price: '' });
    } catch (error) {
      showToast(error.message || "Failed to place order", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter listings based on assetType
  const filteredListings = useMemo(() => {
    return listings.filter(l => l.assetType === assetType);
  }, [listings, assetType]);

  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Activity size={16}/> Market
        </div>
        {/* Asset Type Toggle (HCI: Clear Visibility of Mode) */}
        <div className="toggle-pill">
          <button 
            className={assetType === 'tree' ? 'active' : ''} 
            onClick={() => setAssetType('tree')}
          >
            <Trees size={14} style={{ marginRight: 4 }}/> Trees
          </button>
          <button 
            className={assetType === 'carbon_credit' ? 'active' : ''}
            onClick={() => setAssetType('carbon_credit')}
          >
            <ShieldCheck size={14} style={{ marginRight: 4 }}/> Credits
          </button>
        </div>
      </div>

      <div className="tabs" style={{ marginTop: 12 }}>
        <div className="tab-header">
          {["buy","sell","listings"].map(t => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`tab-btn ${tab === t ? "active" : ""}`}
            >
              {t[0].toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {tab === "buy" && (
          <div style={{ marginTop: 12 }}>
            <div className="grid-2">
              <div>
                <label className="label">
                  {assetType === 'tree' ? "Forest (ID or Name)" : "Project / Vintage"}
                </label>
                <input 
                  className="input" 
                  placeholder={assetType === 'tree' ? "e.g., Mau Complex" : "e.g., VCU-2024-001"} 
                  value={buyForm.forest}
                  onChange={e => setBuyForm({...buyForm, forest: e.target.value})}
                />
              </div>
              <div>
                <label className="label">
                  Quantity ({assetType === 'tree' ? "trees" : "tonnes"})
                </label>
                <input 
                  className="input" 
                  type="number"
                  placeholder={assetType === 'tree' ? "100" : "Whole numbers only"} 
                  value={buyForm.quantity}
                  onChange={e => setBuyForm({...buyForm, quantity: e.target.value})}
                />
                {assetType === 'carbon_credit' && (
                  <div className="hint-text"><Info size={12}/> Whole credits only</div>
                )}
              </div>
              <div>
                <label className="label">Limit Price</label>
                <input 
                  className="input" 
                  type="number"
                  placeholder={assetType === 'tree' ? "142.10" : "35.00"} 
                  value={buyForm.price}
                  onChange={e => setBuyForm({...buyForm, price: e.target.value})}
                />
              </div>
              <div style={{ display: "flex", alignItems: "end", justifyContent: "end" }}>
                <button 
                  className={`btn ${assetType === 'carbon_credit' ? 'btn-info' : 'btn-primary'}`} 
                  onClick={handleBuy}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Placing..." : <><ShoppingCart size={16}/> Buy {assetType === 'tree' ? 'Trees' : 'Credits'}</>}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "sell" && (
          <div style={{ marginTop: 12 }}>
            <div className="grid-2">
              {assetType === 'tree' ? (
                <div>
                  <label className="label">Holding ID</label>
                  <input 
                    className="input" 
                    placeholder="Select holding" 
                    value={sellForm.holdingId}
                    onChange={e => setSellForm({...sellForm, holdingId: e.target.value})}
                  />
                </div>
              ) : (
                <div>
                  <label className="label">Asset ID (Credit)</label>
                  <input 
                    className="input" 
                    placeholder="Select credit batch" 
                    value={sellForm.holdingId}
                    onChange={e => setSellForm({...sellForm, holdingId: e.target.value})}
                  />
                  <div className="hint-text" style={{ color: '#10b981' }}>
                    Available: {availableCredits} tonnes
                  </div>
                </div>
              )}
              
              <div>
                <label className="label">Quantity</label>
                <input 
                  className="input" 
                  type="number"
                  placeholder={assetType === 'tree' ? "50" : "Whole tonnes"} 
                  value={sellForm.quantity}
                  onChange={e => setSellForm({...sellForm, quantity: e.target.value})}
                />
              </div>
              <div>
                <label className="label">Limit Price</label>
                <input 
                  className="input" 
                  type="number"
                  placeholder="142.24" 
                  value={sellForm.price}
                  onChange={e => setSellForm({...sellForm, price: e.target.value})}
                />
              </div>
              <div style={{ display: "flex", alignItems: "end", justifyContent: "end" }}>
                <button 
                  className="btn btn-outline"
                  onClick={handleSell}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Placing..." : `Sell ${assetType === 'tree' ? 'Trees' : 'Credits'}`}
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "listings" && (
          <div style={{ marginTop: 12 }}>
            <div className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr" }}>
              <div>Asset</div>
              <div style={{ textAlign: "right" }}>Qty</div>
              <div style={{ textAlign: "right" }}>Price</div>
              <div style={{ textAlign: "right" }}>Action</div>
            </div>
            <div style={{ height: 8 }} />
            <div className="grid" style={{ gap: 8, maxHeight: '200px', overflowY: 'auto' }}>
              {filteredListings.length === 0 ? (
                <div style={{ gridColumn: "1/-1", textAlign: "center", color: "var(--text-muted)" }}>
                  No active {assetType === 'tree' ? 'tree' : 'credit'} listings
                </div>
              ) : (
                filteredListings.map((l, i) => (
                  <div key={i} className="grid" style={{ gridTemplateColumns: "1fr 1fr 1fr 1fr", color: "var(--text-primary)", fontSize: "0.9rem" }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {l.assetType === 'tree' ? <Trees size={12} color="#10b981"/> : <ShieldCheck size={12} color="#3b82f6"/>}
                      {l.assetType === 'tree' ? 'Tree' : 'Credit'}
                    </div>
                    <div style={{ textAlign: "right" }}>{l.quantity}</div>
                    <div style={{ textAlign: "right" }}>${l.pricePerUnit}</div>
                    <div style={{ textAlign: "right" }}>
                      <button className="btn-xs btn-primary" onClick={() => console.log("Buy listing", l.id)}>Buy</button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const MonitoringPanel = () => {
  const frames = [
    { src: "/frame 1.jpg", label: "Frame 1" },
    { src: "/frame 2.jpg", label: "Frame 2" },
  ];

  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <MapIcon size={16}/> Live Monitoring
      </div>
      <div className="monitor-map" style={{ 
        backgroundImage: "url('/aerial.jpg')", 
        backgroundSize: "cover", 
        backgroundPosition: "center",
        position: "relative",
        minHeight: '150px'
      }}>
        <div style={{
          position: "absolute",
          top: 8,
          right: 8,
          background: "rgba(255, 255, 255, 0.95)",
          padding: "4px 10px",
          borderRadius: "6px",
          fontSize: ".75rem",
          display: "flex",
          alignItems: "center",
          gap: 6,
          color: "var(--text-primary)",
          border: "1px solid var(--panel-stroke)",
          boxShadow: "0 2px 4px rgba(0,0,0,0.1)"
        }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#10b981" }} />
          Live
        </div>
      </div>
    </div>
  );
};

const ExploreForests = ({ forests }) => {
  const navigate = useNavigate();
  
  return (
    <div className="card">
      <div className="card-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <ExternalLink size={16}/> Explore African Forests
      </div>
      <div className="grid-3">
        {forests.length === 0 ? (
           <div style={{ gridColumn: "1/-1", textAlign: "center", padding: "20px" }}>Loading forests...</div>
        ) : (
          forests.map((forest, i) => (
            <motion.div
              key={i}
              whileHover={{ y: -4 }}
              className="explore-card"
            >
              <div style={{ color: "var(--text-primary)", fontWeight: 600 }}>{forest.name}</div>
              <div style={{ fontSize: ".9rem", color: "var(--text-secondary)" }}>
                {forest.metrics?.totalAreaHectares} hectares • {forest.location?.country}
              </div>
              <div style={{ marginTop: 12, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ fontSize: ".9rem", color: "var(--text-secondary)" }}>
                   {forest.status}
                </div>
                <button 
                  className="btn btn-outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/forest/${forest.id}`);
                  }}
                >
                  View <ChevronRight size={16} style={{ marginLeft: 6 }} />
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const userId = currentUser?.id;

  // Local UI state
  const [toast, setToast] = useState(null);

  // Data Hooks
  const { profile, portfolioSummary, carbonWallet, loading: profileLoading } = useUserProfile(userId);
  const { investments, totalTrees, totalValue, loading: investLoading } = useInvestments(userId);
  const { totalTonnes, loading: creditLoading } = useCarbonCredits(userId);
  const { forests, loading: forestLoading } = useForests();
  const { listings, loading: marketLoading } = useMarketListings();
  const { notifications, unreadCount } = useNotifications(userId);

  const isLoading = profileLoading || investLoading || creditLoading || forestLoading;

  const showToast = (message, type) => {
    setToast({ message, type });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="app-container">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      
      {/* Navbar */}
      <div className="navbar">
        <div 
          className="logo" 
          style={{ height: '60px', overflow: 'hidden', display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <img 
            src="/Lateral Original.png" 
            alt="Sango" 
            style={{ 
              height: '110px', 
              width: 'auto',
              objectFit: 'contain',
              objectPosition: 'center'
            }}
          />
        </div>
        <div className="nav-right">
          <div className="search-wrap">
            <Search size={16} className="search-icon" />
            <input className="search-input" placeholder="Search forests, assets, orders" />
          </div>
          <button className="btn btn-ghost" style={{ position: 'relative' }}>
            <Bell size={16}/> Alerts
            {unreadCount > 0 && (
              <span style={{
                position: 'absolute', top: 0, right: 0, 
                background: 'red', color: 'white', 
                borderRadius: '50%', width: '16px', height: '16px',
                fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {unreadCount}
              </span>
            )}
          </button>
          <button className="btn btn-primary" onClick={logout}>Log Out</button>
        </div>
      </div>

      {/* Body grid */}
      <div className="container grid" style={{ gridTemplateColumns: "260px 1fr" }}>
        {/* Sidebar */}
        <aside className="sidebar">
          <button className="active"><LineChart size={16}/> Dashboard</button>
          <button><Trees size={16}/> Forests</button>
          <button><Leaf size={16}/> Carbon Credits</button>
          <button><Activity size={16}/> Markets</button>
          <button><ShieldCheck size={16}/> Compliance</button>
          <div style={{ height: 16 }} />
          <div className="card">
            <div className="card-title">Quick Stats</div>
            <div style={{ display: "grid", gap: 8, fontSize: ".95rem", color: "var(--text-primary)" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-secondary)" }}>Account Balance</span>
                <span>${(portfolioSummary?.accountBalance / 100)?.toLocaleString() || '0.00'}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-secondary)" }}>Trees Owned</span>
                <span>{portfolioSummary?.totalTreesOwned || totalTrees}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: "var(--text-secondary)" }}>Credits (tCO₂e)</span>
                <span>{portfolioSummary?.totalCarbonCredits || totalTonnes}</span>
              </div>
            </div>
          </div>
        </aside>

        {/* Main content */}
        <main className="grid" style={{ gap: 24 }}>
          {/* KPIs */}
          <div className="grid-4">
            <KPI 
              title="Portfolio Value" 
              value={`$${(portfolioSummary?.currentPortfolioValue || totalValue)?.toLocaleString()}`} 
              delta={portfolioSummary?.lastUpdated ? "Updated just now" : null} 
              up 
            />
            <KPI 
              title="Trees Owned" 
              value={portfolioSummary?.totalTreesOwned || totalTrees} 
            />
            <KPI 
              title="Carbon Credits" 
              value={`${portfolioSummary?.totalCarbonCredits || totalTonnes} tCO₂e`} 
              subValue={`Pending: ${carbonWallet?.pendingMassKg?.toFixed(1) || 0} kg`}
              up 
            />
            <KPI 
              title="Available Cash" 
              value={`$${(portfolioSummary?.accountBalance / 100)?.toLocaleString() || '0.00'}`} 
            />
          </div>

          {/* Row: Chart + Holdings */}
          <div className="grid" style={{ gridTemplateColumns: "2fr 1fr", gap: 20 }}>
            <PortfolioChart />
            <Holdings investments={investments} forests={forests} />
          </div>

          {/* Row: Market + Credits + Monitoring */}
          <div className="row row-3">
            <MarketPanel 
              listings={listings} 
              onOrder={() => {}} 
              showToast={showToast} 
              availableCredits={totalTonnes}
            />
            <CreditsChart carbonWallet={carbonWallet} />
            <MonitoringPanel />
          </div>

          {/* Explore */}
          <ExploreForests forests={forests} />
        </main>
      </div>

      {/* Footer */}
      <div className="footer">© {new Date().getFullYear()} Sango • Invest in African Living Assets</div>
    </div>
  );
}
